import { Component, inject, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { ExperimentService } from '@services/experiment';
import { ApiClientConstant } from '@cureskin/api-client/src/constant/api-client.constant';
import { Router } from '@angular/router';
import { Catalog, ReorderKitDetail } from '@cureskin/api-client/src/server-api';
import { EventLoggerService } from '@services/event-logger-service';

@Component({
  selector: 'regimen-reorder-banner',
  imports: [ButtonCtaComponent, DatePipe],
  templateUrl: './regimen-reorder-banner.component.html',
})
export class RegimenReorderBannerComponent {

  private readonly experimentService = inject<ExperimentService>(ExperimentService);
  private readonly router = inject<Router>(Router);
  private readonly eventLoggerService = inject<EventLoggerService>(EventLoggerService);
  readonly ApiClientConstant = ApiClientConstant;

  bannerDetail = input<ReorderKitDetail>();
  orderState = input<string>();

  ngOnInit(): void {
    this.eventLoggerService.cleverTapEvent('impression', JSON.stringify({
      name: 'regimen_reorder_banner',
      totalMrp: this.bannerDetail()?.totalMrp,
      totalSP: this.bannerDetail()?.totalSP,
      discountInRupees: this.bannerDetail()?.discountInRupees,
      regimenClass: this.bannerDetail()?.regimenClass
    }));
  }

  private get daysSinceUpdate(): number {
    if (!this.bannerDetail()?.lastUpdatedAt) return -1;
    const diffMs = Date.now() - new Date(this.bannerDetail()!.lastUpdatedAt).getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  get isUpdatedToday(): boolean {
    return this.daysSinceUpdate === 0;
  }

  get isUpdatedYesterday(): boolean {
    return this.daysSinceUpdate === 1;
  }

  get isUpdatedBeforeYesterday(): boolean {
    return !this.isUpdatedToday && !this.isUpdatedYesterday;
  }

  get lastUpdatedAtDate(): Date | null {
    return this.bannerDetail()?.lastUpdatedAt
      ? new Date(this.bannerDetail()!.lastUpdatedAt)
      : null;
  }

  onBuyNow(): void {
    const productIds = this.bannerDetail()?.products.map((product: Catalog ) => product.objectId);
    const productIdsString = productIds.toString();
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({
      name: 'regimen_reorder_banner_buy_now',
      totalMrp: this.bannerDetail()?.totalMrp,
      totalSP: this.bannerDetail()?.totalSP,
      discountInRupees: this.bannerDetail()?.discountInRupees,
      regimenClass: this.bannerDetail()?.regimenClass
    }));
    this.navigateToCheckout(productIdsString);
  }

  navigateToCheckout(productIdsString: string): void {
    this.router.navigate(['/checkout'], {
      queryParams: {
        products: productIdsString,
        type: ApiClientConstant.Order.Type.PRODUCT,
        useCureSkinCash: String(this.experimentService.shouldUseCureSkinCash(this.orderState())),
        isRegimenReorder: true.toString(),
        regimenClass: this.bannerDetail()?.regimenClass
      }
    });
  }

  get doctorFirstName(): string {
    return this.bannerDetail()?.lastUpdatedBy?.split(' ').slice(0, 2).join(' ') || '';
  }
}
